import { Form, Formik } from 'formik'
import { observer } from 'mobx-react-lite'
import React, { useContext, useEffect, useState } from 'react'
import { InfluencerContext } from '../../../../../contexts/InfluencerContext'
import ReactSelect from '../../../../FormComponents/ReactSelect'
import InfoTooltip from '../../../../FormComponents/InfoTooltip'
import Modal from '../../../../Modal/Modal'
import * as Yup from 'yup'
import axiosService from '../../../../../utils/AxiosService'
import { useParams } from 'react-router-dom'
import { toastError, toastSuccess } from '../../../../FormComponents/Toasts'

export default observer(function InfluencerModal({ modalOpen, setModalOpen }) {
  const { id } = useParams()
  const { influencer, getInfluencer, getAuthentications, getRatings } = useContext(InfluencerContext)
  const [influencerOptions, setInfluencerOptions] = useState([])
  const [pendingConfirmation, setPendingConfirmation] = useState(null)

  const submitChangeInfluencer = (influencer_id, confirmActiveCampaignMove = false) => {
    axiosService.patch(`/api/project_manager/authentications/${id}/change_influencer`, {
      authentication: { influencer_id, confirm_active_campaign_move: confirmActiveCampaignMove }
    })
    .then(authentication => {
      if(authentication.influencer_id !== influencer.id) {
        getInfluencer()
        getAuthentications()
        getRatings()
      }
      toastSuccess('Influencer changed')
      setPendingConfirmation(null)
      setModalOpen(false)
    })
    .catch(({ data }) => {
      if (data?.active_campaigns?.length) {
        setPendingConfirmation({ influencer_id, activeCampaigns: data.active_campaigns })
      } else {
        toastError(data?.error)
      }
    })
  }

  const changeInfluencer = ({ influencer_id }) => submitChangeInfluencer(influencer_id)

  const getInfluencers = () => {
    axiosService.get(`/api/project_manager/influencers`)
    .then((influencers) => setInfluencerOptions(influencers.map((influencer => {
      return { label: `${influencer.full_name} ${influencer.email || 'No email address' }`, value: influencer.id }
    }))))
  }

  useEffect(() => getInfluencers(), [])

  return (
    <Modal isOpen={modalOpen} onClose={() => { setPendingConfirmation(null); setModalOpen(false) }}>
      <Modal.Header>
        <span className="inline-flex items-center gap-2">
          Change Influencer
          <InfoTooltip text="Moves this account to a different influencer profile. Its campaign history and budget data move with it." />
        </span>
      </Modal.Header>
      <Modal.Body>
        <div className="w-96">
          {pendingConfirmation ? (
            <div>
              <p className="font-bold dark:text-white">This account is linked to active campaign(s):</p>
              <ul className="list-disc pl-5 my-2 dark:text-white">
                {pendingConfirmation.activeCampaigns.map(campaign => <li key={campaign.id}>{campaign.name}</li>)}
              </ul>
              <p className="dark:text-white">Moving it will move that campaign history to the new influencer too. Continue?</p>
              <div className="flex justify-between pt-4">
                <button
                  type="button"
                  className="rb-btn-cancel font-bold text-md p-2 px-4 rounded-lg"
                  onClick={() => setPendingConfirmation(null)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="flex items-center gap-2 bg-rb-green text-white rounded-lg p-2 px-4 text-center active:shadow-outer active:shadow-rb-green border-rb-green dark:text-rb-dark-mode"
                  onClick={() => submitChangeInfluencer(pendingConfirmation.influencer_id, true)}
                >
                  Yes, move it anyway
                </button>
              </div>
            </div>
          ) : (
            <Formik
              validationSchema={
                Yup.object().shape({ influencer_id: Yup.number().required('This field is required.') })}
              initialValues={{
                influencer_id: influencer.id
              }}
              onSubmit={changeInfluencer}
            >
              {({ handleBlur, handleChange, values, errors, touched, setFieldValue }) => (
                <Form>
                  <div className="form-item-container w-full">
                    <div className="mb-4">
                      <ReactSelect error={touched.influencer_id && errors.influencer_id} label="Influencer" name="influencer_id"
                          onChange={influencer_id => setFieldValue('influencer_id', influencer_id.value, true)}
                          onBlur={handleBlur} value={values.influencer_id} options={influencerOptions} />
                    </div>
                    <button
                      type="submit"
                      className="submit text-left w-full text-rb-green border-2 rounded-full w-60 py-4 text-center active:shadow-outer active:shadow-rb-green border-rb-green"
                    >
                      Change Influencer
                    </button>
                  </div>
                </Form>
              )}
            </Formik>
          )}
        </div>
      </Modal.Body>
    </Modal>
  )
})